/**
 * Telling the Worker what happened, so that somebody can find out.
 *
 * The browser half of docs/analytics.md; the server half is worker/events.ts.
 * Between them they answer the question the product could not previously ask
 * about itself — of the people who arrive, how many get a machine to boot,
 * and how far do the ones who do actually get.
 *
 * ## It sends into the void
 *
 * Nothing here is awaited by a screen, no failure of it is shown to anybody,
 * and no caller can tell whether a single event was ever recorded. Every
 * function in this file returns `void` and swallows everything, which is not
 * laziness: the alternative is a product whose level list can be broken by an
 * analytics endpoint, and there is no version of that trade worth making. The
 * chat made the same one in the other direction (`quietly`, worker/
 * chat-store.ts) and for the same reason.
 *
 * ## It batches, because a tab is a thing that closes
 *
 * Events go into an array and leave in groups: a few seconds after the first
 * one, and — the flush that matters — the moment the page is hidden. A
 * learner who reads the level list, starts level 0, gets stuck and closes the
 * tab produces the single most valuable row in the table, and it is produced
 * at the exact moment a normal `fetch` is cancelled. `sendBeacon` is the only
 * transport the platform guarantees will survive that, so it is the one used
 * whenever the page is going away.
 *
 * ## What it knows
 *
 * A random number this browser generated for itself, which level of which
 * track the learner is on, and one word out of ten about what kind of browser
 * it is. It does not read the account, the name, the email, the page URL, the
 * referrer or the user agent beyond the ten-word reduction below, and the
 * Worker does not write the IP it cannot help receiving. That is not a policy
 * sitting on top of a system that could do more; it is the whole of what this
 * file is able to say, which is the only kind of privacy claim worth printing.
 *
 * The track belongs on that list without widening it. It says which course a
 * level was in, which is a fact about the level and not about the person
 * taking it — a learner moves between tracks, and nothing here remembers that
 * they were ever on one.
 */

/* ── Who this browser is, to itself ──────────────────────────────────────── */

import { beginPresence, type PresenceArea } from './live-presence';

/** Presence shares the existing anonymous browser id and privacy opt-out. */
export function startPresence(area: PresenceArea): void {
  try { beginPresence(area, () => ({ visitor: visitorId(), browser: browserLabel() }), optedOut); }
  catch { /* Nothing here is required for the page to work. */ }
}

const VISITOR_KEY = 'engineeros.visitor';

/**
 * A random 128-bit id, made once and kept in localStorage.
 *
 * It is not an account id and is deliberately never joined to one. The
 * temptation is obvious — the Worker knows perfectly well who is signed in on
 * this request, and stamping the user id on the row would cost nothing and
 * would let somebody ask "which of my paying customers got stuck". The reason
 * not to is that the moment this table can name a person it stops being a
 * count of behaviour and becomes a record about individuals, with everything
 * that follows from that: a subject access request that has to include it, a
 * deletion that has to reach it, and a sentence in a privacy policy that is
 * much longer and much less reassuring than the one that is true today.
 *
 * The cost of that choice is real and worth stating: signing in on a second
 * machine makes somebody a second visitor, and so does clearing site data. So
 * the funnel slightly overcounts people and undercounts how far each one got.
 * For the questions this exists to answer — where do learners stall, does the
 * machine boot, is the offer seen — that error is a rounding error, and it is
 * the correct thing to be wrong about.
 *
 * Storage can throw or be empty in a private window, and the fallback is an
 * id that lives only as long as the tab. That browser is then counted as a
 * new visitor on every load, which is the honest answer for somebody who has
 * asked their browser to forget them between visits.
 */
let memoryVisitor: string | null = null;

function visitorId(): string {
  if (memoryVisitor) return memoryVisitor;
  const fresh = (): string =>
    Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  try {
    const saved = localStorage.getItem(VISITOR_KEY);
    if (saved && /^[0-9a-f]{32}$/.test(saved)) {
      memoryVisitor = saved;
      return saved;
    }
    const made = fresh();
    localStorage.setItem(VISITOR_KEY, made);
    memoryVisitor = made;
    return made;
  } catch {
    // Private mode, or storage switched off. One id for this tab, and no
    // attempt to persist it anywhere else.
    memoryVisitor = fresh();
    return memoryVisitor;
  }
}

/**
 * This machine, in one word out of ten.
 *
 * The whole point is how little it says. A user agent string identifies a
 * browser precisely enough to pick one person out of a crowd, and none of
 * that precision answers the only question worth asking — whether there is a
 * browser the product does not work in. `firefox-win` answers it. The list is
 * duplicated in worker/events.ts, which validates against it, so a word this
 * function invents would be stored as nothing rather than as itself.
 *
 * Order matters in the tests below: Edge's user agent contains "Chrome", and
 * Chrome's contains "Safari". Phones and tablets are collapsed into `mobile`
 * before anything else is asked, because the product does not run on them and
 * the only interesting fact is how many people try.
 */
function browserLabel(): string {
  try {
    const ua = navigator.userAgent;
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return 'mobile';
    const platform = /Windows/i.test(ua) ? 'win' : /Macintosh|Mac OS X/i.test(ua) ? 'mac' : /Linux|X11/i.test(ua) ? 'linux' : null;
    if (!platform) return 'other';
    if (/Edg\//i.test(ua)) return platform === 'win' ? 'edge-win' : 'other';
    if (/Firefox\//i.test(ua)) return `firefox-${platform}`;
    if (/Chrome\//i.test(ua)) return platform === 'win' || platform === 'mac' || platform === 'linux' ? `chrome-${platform}` : 'other';
    if (/Safari\//i.test(ua)) return platform === 'mac' ? 'safari-mac' : 'other';
    return 'other';
  } catch {
    return 'other';
  }
}

/* ── What may be said ────────────────────────────────────────────────────── */

/**
 * The twenty-six words, exactly as worker/events.ts spells them. Repeated here
 * rather than imported because the Worker is not a package this bundle
 * depends on — the one shared vocabulary in this codebase, the chat protocol,
 * earned its package by being spoken in both directions, and this is spoken
 * in one. A word that drifts is stored as nothing, and the Worker's smoke
 * test asserts the list.
 */
export type EventKind =
  /** One of our own links was followed; `detail` is its `?from=` word (see `noteArrival`). */
  | 'arrive'
  /** The landing page was opened; `detail` is the browser label (see `noteLanding`). */
  | 'land'
  /** The landing page's guided first command was passed; `detail` typed or shown, `value` wrong tries first. */
  | 'landing-try'
  | 'visit'
  | 'boot-start'
  | 'boot-ready'
  | 'boot-fail'
  | 'warmup-skip'
  | 'level-start'
  | 'level-check'
  | 'hint'
  /** A turn asked of the online mentor; `detail` says what came of it, including `sign-in`. */
  | 'mentor-ask'
  | 'level-clear'
  | 'level-reset'
  | 'level-skip'
  | 'offer-seen'
  | 'checkout-start'
  | 'paid'
  /** The editor's Run or Run tests, refused as `busy`, or a `path:line` followed from the terminal (`trace`). */
  | 'editor-run'
  /** A chapter's explainer started from its first scene, and watched to its last (components/explainer/Player.tsx). */
  | 'explainer-play'
  | 'explainer-end'
  /**
   * One step of Iris in agent mode (components/play/Iris.tsx); `detail` is
   * proposed, ran, rejected, noted, check or done, or the refusal's word.
   */
  | 'iris-step'
  /**
   * One decision about a designed agent's message (docs/designed-agents.md):
   * `detail` is `<beat id>:<move>`, move one of approve, reject, reply0-3. The
   * Worker keeps it only for a beat that exists on that level.
   */
  | 'agent-decision'
  /** The tab stopped being at a place; `detail` is the place, `value` the seconds visible there (see `setPlace`). */
  | 'leave'
  /** A practice track (`/foundations`, `/civil`, `/power`): `detail` is `<track>:open`, `<track>:<step>` when passed, or `<track>:done`. */
  | 'practice'
  /** A discipline asked for at the end of Foundations; `detail` software, power or civil. */
  | 'discipline-vote';

/**
 * One event, from the moment it is recorded until the flush that takes it.
 *
 * The four optional fields are the four columns a row may fill, and the path
 * between here and the wire checks none of them: they are queued, batched and
 * serialised exactly as the caller gave them. What may actually be stored is
 * the Worker's to say (worker/events.ts), which is where the closed vocabulary
 * lives.
 *
 * `track` sits beside `level` rather than instead of it because a level
 * number is a position **within** a track, not a position in the course. Once
 * Act III branches, each specialization numbers its own levels from zero
 * (docs/curriculum.md §4-5), so backend's first lesson and frontend's first
 * lesson and the general track's first lesson are all "level 0" — and a
 * funnel grouped on the number alone would add the three of them together and
 * report a drop-off curve belonging to no course anybody took. That is the
 * worst shape a reporting bug can have: it does not fail, it answers.
 *
 * There is deliberately no list of tracks in this file, which is the one
 * respect in which this field does not follow `detail` and the boot-failure
 * codes. Those are narrowed in `bootFailed` below as well as in the Worker, so
 * that a new failure mode arrives as `other` instead of vanishing. A track is
 * the opposite case. The words come
 * from the mission catalogue, a new one ships in a mission file long before
 * anybody edits an allowlist, and the Worker already drops a word it has not
 * learned while keeping the row — so an unrecognised track arrives as a
 * visible bucket with no track on it, which is the "new client, old Worker"
 * case that rule exists for. A second copy of the list here would turn that
 * bucket into silence during exactly the deploy somebody is watching it.
 */
interface QueuedEvent {
  kind: EventKind;
  level?: number;
  track?: string;
  detail?: string;
  value?: number;
}

/* ── Whether to say anything at all ──────────────────────────────────────── */

/**
 * Global Privacy Control, and the older Do Not Track beside it.
 *
 * Neither signal legally reaches something that collects no personal
 * information and shares nothing with anybody, so honouring them here is a
 * choice rather than a requirement. It is made for two reasons. The audience
 * for this product is software engineers, who are the population most likely
 * to have set one of these deliberately, and ignoring a preference somebody
 * went out of their way to express because it does not technically bind you
 * is the sort of thing that is true and still wrong. And it costs a few
 * counted visitors: adoption is low single digits, the funnel reads in
 * proportions, and a proportion survives a uniformly missing slice.
 */
function optedOut(): boolean {
  try {
    const nav = navigator as Navigator & { globalPrivacyControl?: boolean; doNotTrack?: string };
    // `window.doNotTrack` is the older spelling some browsers used instead of
    // the one on `navigator`, and it is not in the DOM types because it is
    // not in any current standard. Both are read anyway: honouring a signal
    // that has fallen out of the specification costs a cast, and ignoring one
    // somebody set on purpose costs more than that.
    const legacy = (window as Window & { doNotTrack?: string }).doNotTrack;
    return nav.globalPrivacyControl === true || nav.doNotTrack === '1' || legacy === '1'
      || automated() || uncounted();
  } catch {
    return false;
  }
}

/**
 * Browsers that are not learners: driven ones, and the ones we marked.
 *
 * At a few visitors a day our own headless test runs and the owner's tabs
 * were a visible share of "online now" and of the funnel. A driven browser
 * says so in `navigator.webdriver` (and old headless Chrome in its user
 * agent); everybody else on the team opens the site once with `?count=off`,
 * which this browser then remembers until `?count=on`. Nothing is sent to
 * say so — the browser simply stops reporting, like an opt-out.
 */
const UNCOUNTED_KEY = 'engineeros.uncounted';

function automated(): boolean {
  return navigator.webdriver === true || /HeadlessChrome/.test(navigator.userAgent);
}

function uncounted(): boolean {
  try { return window.localStorage.getItem(UNCOUNTED_KEY) === '1'; } catch { return false; }
}

/** Reads and removes `?count=off|on` before anything else reads the URL. */
export function applyCountSwitch(): void {
  try {
    const url = new URL(window.location.href);
    const value = url.searchParams.get('count');
    if (value !== 'off' && value !== 'on') return;
    if (value === 'off') window.localStorage.setItem(UNCOUNTED_KEY, '1');
    else window.localStorage.removeItem(UNCOUNTED_KEY);
    url.searchParams.delete('count');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    console.info(`EngineerOS: this browser is ${value === 'off' ? 'no longer' : 'now'} counted in analytics.`);
  } catch { /* Measurement must never affect the page. */ }
}

/* ── The queue ───────────────────────────────────────────────────────────── */

const ENDPOINT = '/api/events';
/** Long enough that a burst of objectives passing is one request, short enough that little is lost to a crash. */
const LINGER_MS = 3_000;
/**
 * The Worker's own cap (MAX_BATCH). Flush at it rather than sending a batch it
 * will truncate.
 *
 * It is the batch that is bounded here and not the bytes, and the two stay far
 * apart on purpose: thirty-two events, each a kind, a small number, a track
 * slug and a word from an allowlist, is a payload of about three kilobytes
 * against the Worker's eight-kilobyte body cap. There is no length in this
 * file that a new field could push past — every field is a short word from a
 * closed list or an integer — so the cap is the Worker's to enforce and this
 * count never has to be recomputed to protect it.
 */
const MAX_QUEUE = 32;

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
/**
 * Set once the route has answered 404, which is what a deployment with
 * `ANALYTICS = "off"` looks like from here — and also what every deployment
 * that has not applied the migration looks like. After that this module does
 * nothing at all for the life of the page, so a switched-off deployment is
 * not paying for a request per tab either.
 */
let stopped = false;
/**
 * Whether the first flush has happened. It goes by `fetch`, whose status can
 * be read, precisely so that {@link stopped} can be learned; every flush
 * after it may use `sendBeacon`, which cannot report anything but which is
 * the only transport that survives a closing tab.
 */
let probed = false;

function schedule(): void {
  if (timer !== null) return;
  timer = setTimeout(() => {
    timer = null;
    flush(false);
  }, LINGER_MS);
}

/**
 * Send what is queued.
 *
 * `leaving` is the difference between the two transports. A page that is
 * being hidden or unloaded gets `sendBeacon`, which hands the bytes to the
 * browser to deliver after the document is gone; anything else gets a
 * `fetch`, which can be read. `keepalive` is set on the fetch as well, so
 * even the ordinary path survives a navigation that starts mid-request.
 *
 * The queue is emptied before the send rather than after it, and nothing is
 * ever put back. A retry queue would mean unbounded memory in a tab that has
 * been open all afternoon with no network, duplicate rows whenever a retry
 * raced a success, and a decision about ordering — all to recover data whose
 * entire purpose is to be counted in aggregate. Losing a batch costs a
 * fraction of a percent of a number that is read as a proportion.
 */
function flush(leaving: boolean): void {
  if (stopped || queue.length === 0) return;
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  const events = queue;
  queue = [];

  let payload: string;
  try {
    payload = JSON.stringify({ visitor: visitorId(), events });
  } catch {
    return;
  }

  try {
    if (leaving && probed && typeof navigator.sendBeacon === 'function') {
      // A Blob rather than a bare string, so the request carries a content
      // type the Worker's JSON parse is happy with. A beacon of type
      // text/plain is a request some servers will not read, and there is no
      // status to tell us if this one did not.
      navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'application/json' }));
      return;
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
      // No cookie. The route neither reads nor needs a session, and sending
      // one would make this a request that carries an identity — which is the
      // one thing the design is built not to do.
      credentials: 'omit',
      cache: 'no-store',
    })
      .then((res) => {
        probed = true;
        if (res.status === 404) stopped = true;
      })
      .catch(() => {
        // Offline, blocked by an extension, or the Worker is down. All three
        // are the same thing here, and the same thing is nothing.
      });
  } catch {
    /* sendBeacon throws on some blocked-by-extension paths. Nothing to do. */
  }
}

/**
 * Record one thing that happened.
 *
 * The only function most callers need. It never throws, never blocks, and
 * never tells you whether it worked.
 *
 * A caller that names a `level` should name the `track` it is a level of, and
 * should read both off the mission it already has in its hand. Neither is
 * remembered by this module between calls, and the track in particular must
 * not be: it is a property of the level, not of the session or of the learner,
 * and a learner moves between tracks freely — the choice is meant to be cheap
 * (docs/curriculum.md §4). A cached track would survive the level it was true
 * for and quietly file the next one under the last one's course, which is a
 * report that is wrong in a way nothing in it admits to.
 */
export function track(kind: EventKind, fields: { level?: number; track?: string; detail?: string; value?: number } = {}): void {
  if (stopped || optedOut()) return;
  queue.push({ kind, ...fields });
  // A full queue goes now rather than in three seconds, because the next
  // event would be dropped by the Worker's own cap.
  if (queue.length >= MAX_QUEUE) flush(false);
  else schedule();
}

/**
 * Which of our own links brought this visit: the `?from=` word, as the
 * invitation emails write it (worker/events.ts `ARRIVALS` is the list).
 *
 * Called first thing in main.tsx, on every page, because an invitation opens
 * the landing page and the point is to follow the people who never press its
 * button too. The parameter comes off the address bar whatever it says, so a
 * link somebody copies on to a friend is not counted as the campaign's, and a
 * reload is not a second arrival. A word not on the list is dropped here
 * rather than sent to be dropped there. Sent straight away, because the
 * landing page does not start a session and has nothing else to flush it.
 */
const ARRIVALS = new Set(['invite-club', 'invite-learner', 'invite-followup', 'scholarship',
  'social-youtube', 'social-instagram', 'social-x', 'social-reddit']);

export function noteArrival(): void {
  try {
    const url = new URL(window.location.href);
    const from = url.searchParams.get('from');
    if (from === null) return;
    url.searchParams.delete('from');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    if (optedOut() || !ARRIVALS.has(from)) return;
    track('arrive', { detail: from });
    flush(true);
  } catch {
    /* Nothing here is required for the page to work. */
  }
}

/**
 * The landing page was opened: the `land` event, with the same one-word
 * browser label `visit` carries and nothing else.
 *
 * `/` used to record nothing, so the top of the funnel was invisible: how many
 * people read the pitch, how many of them were on a phone, and how many went
 * on to press its button (which is `visit`, on the course) could not be told
 * apart from nobody coming at all. Sent straight away, as `noteArrival` is,
 * because the landing page starts no session and has nothing else to flush
 * it. Called from main.tsx only when the landing page is what mounts; a
 * learner's public page, the agent page and the Blobor showcase still record
 * nothing.
 */
export function noteLanding(): void {
  try {
    if (optedOut()) return;
    track('land', { detail: browserLabel() });
    flush(true);
    watchLeaving();
    setPlace({ where: 'landing' });
  } catch {
    /* Nothing here is required for the page to work. */
  }
}

/**
 * A practice track was opened (`/foundations`, `/civil`, `/power`,
 * docs/foundations.md): the `practice` event with `<track>:open`, sent at
 * once like `land`, and a flush whenever the tab is hidden so a step passed
 * just before leaving is not lost.
 *
 * Not `watchLeaving`: that reports how long the tab stayed at a place, and
 * the places are a closed list these pages are not on. It starts no session
 * for the reason the landing page does not — it boots no machine, so it is
 * not a `visit`.
 */
export function noteFoundations(which: string): void {
  try {
    if (optedOut()) return;
    track('practice', { detail: `${which}:open` });
    flush(true);
    const out = (): void => flush(true);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') out();
    });
    window.addEventListener('pagehide', out);
  } catch {
    /* Nothing here is required for the page to work. */
  }
}

/**
 * Start the session: the `visit` event, and the listeners that make a closing
 * tab tell the truth (`watchLeaving`, below).
 *
 * Called once from main.tsx, before React mounts, so that a visit is recorded
 * even for a browser that is about to fail the support check and render
 * nothing but an apology — which is exactly the visit worth knowing about.
 */
export function startSession(): void {
  if (optedOut()) return;
  track('visit', { detail: browserLabel() });
  try {
    watchLeaving();
    setPlace({ where: 'course' });
  } catch {
    /* Nothing here is required for the page to work. */
  }
}

/* ── Where the tab is, and for how long ──────────────────────────────────── */

/**
 * The five places somebody can stop (worker/events.ts `PLACES`): the landing
 * page, the course with no level open, the machine starting, a level, and the
 * phone page.
 *
 * Every other event is something somebody did, so a learner who opened level
 * 0, read it for six minutes and closed the tab used to look exactly like one
 * who closed it at once: one `level-start`, then nothing. `leave` is the
 * missing number. It is sent when the tab is hidden and whenever the place
 * changes, with the seconds the tab was **visible** there since the last one —
 * visible, because a tab left open behind another for an hour is not an hour
 * anybody spent here. A browser's last `leave` is where it stopped, and the
 * sum of its values is how long it stayed.
 */
type Place =
  | { where: 'landing' | 'course' | 'boot' | 'phone' }
  | { where: 'level'; level: number; track: string };

let place: Place | null = null;
/** `performance.now()` when the tab last became visible at this place, or null while it is hidden. */
let visibleSince: number | null = null;
/** Visible time at this place not yet reported. */
let unreported = 0;
let watching = false;

function samePlace(a: Place | null, b: Place): boolean {
  if (!a || a.where !== b.where) return false;
  return a.where !== 'level' || (b.where === 'level' && a.level === b.level && a.track === b.track);
}

/** Queue a `leave` for the current place with what has accrued, and start counting afresh. */
function reportPlace(): void {
  if (!place) return;
  const now = performance.now();
  if (visibleSince !== null) {
    unreported += now - visibleSince;
    visibleSince = now;
  }
  const seconds = Math.round(unreported / 1000);
  unreported = 0;
  track('leave', place.where === 'level'
    ? { detail: 'level', level: place.level, track: place.track, value: seconds }
    : { detail: place.where, value: seconds });
}

function setPlace(next: Place): void {
  try {
    if (samePlace(place, next)) return;
    // A place left while the tab was hidden was already reported when it was
    // hidden; a second, empty row would only move "where they stopped" to a
    // place nobody saw.
    if (place && (visibleSince !== null || unreported >= 500)) reportPlace();
    place = next;
    unreported = 0;
    visibleSince = document.visibilityState === 'visible' ? performance.now() : null;
  } catch {
    /* Nothing here is required for the page to work. */
  }
}

/**
 * The listeners that make a closing tab tell the truth, installed once by
 * whichever of `noteLanding` and `startSession` runs.
 *
 * `visibilitychange` rather than `unload`: `unload` does not fire reliably on
 * mobile or when a tab is discarded, and registering a handler for it opts
 * the page out of the back/forward cache in every current browser. `pagehide`
 * covers the bfcache case `visibilitychange` does not, and reports nothing
 * twice, because a tab already hidden has nothing visible left to report.
 */
function watchLeaving(): void {
  if (watching) return;
  watching = true;
  const hide = (): void => {
    try {
      if (visibleSince !== null) {
        reportPlace();
        visibleSince = null;
      }
    } catch { /* fall through to the flush */ }
    flush(true);
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') hide();
    else if (visibleSince === null) visibleSince = performance.now();
  });
  window.addEventListener('pagehide', hide);
}

/** A level opened (state/useSession.ts), or closed when `null`, back to the course. */
export function levelPlace(open: { level: number; track: string } | null): void {
  if (!watching) return;
  setPlace(open ? { where: 'level', ...open } : { where: 'course' });
}

/**
 * How long the machine took, measured from the moment the boot began.
 *
 * A module-level mark rather than a timestamp passed through four components,
 * because the two ends of this measurement are in different files and the
 * value is the only thing in this module that cannot be recomputed after the
 * fact. `performance.now()` rather than `Date.now()` so that a clock
 * adjustment mid-boot cannot produce a negative duration or a three-hour one.
 */
let bootMark: number | null = null;

export function bootStarted(): void {
  bootMark = performance.now();
  track('boot-start');
  if (watching) setPlace({ where: 'boot' });
}

export function bootReady(): void {
  const ms = bootMark === null ? null : Math.round(performance.now() - bootMark);
  bootMark = null;
  track('boot-ready', ms === null ? {} : { value: ms });
  if (watching && place?.where === 'boot') setPlace({ where: 'course' });
}

/**
 * The machine did not start, and why in one word.
 *
 * The code is passed through the same allowlist the Worker enforces, so that
 * a new failure mode in the VM package arrives as `other` — a visible,
 * unexplained rise — rather than being silently dropped at the far end and
 * looking like it never happened. `catalogue` and `script` are not the VM's:
 * they are the level list and a throw of ours with no code on it
 * (worker/events.ts, `BOOT_FAILURES`, says why they were split out of `boot`).
 *
 * The wait so far rides along when the boot had started, measured from the
 * same mark as `bootReady` — a failure before `bootStarted` (the support
 * check) has no wait to report.
 */
const KNOWN_FAILURES = new Set([
  'unsupported',
  'memory',
  'phone',
  'manifest',
  'fingerprint',
  'download',
  'boot',
  'dead',
  'protocol',
  'timeout',
  'destroyed',
  'catalogue',
  'script',
  'storage',
  'out-of-memory',
  'network',
]);

/**
 * A word for a throw that carried no code of its own, from its type alone.
 *
 * The message is read here, in the page, only to choose among three words;
 * it never leaves the browser (docs/analytics.md: nothing free-form is sent).
 * Anything not recognised stays `script`, which still means "a bug of ours".
 */
export function uncodedFailure(err: unknown): string {
  const e = err as { name?: unknown; message?: unknown } | null;
  const name = typeof e?.name === 'string' ? e.name : '';
  const message = typeof e?.message === 'string' ? e.message : '';
  if (/^(QuotaExceededError|UnknownError|InvalidStateError|VersionError|DataError|NotReadableError)$/.test(name) || /indexeddb|idb|quota|storage/i.test(message)) return 'storage';
  if (err instanceof RangeError || /out of memory|allocation failed|cannot allocate/i.test(message)) return 'out-of-memory';
  if ((err instanceof TypeError && /fetch|network|load failed/i.test(message)) || name === 'NetworkError') return 'network';
  return 'script';
}

export function bootFailed(code: string): void {
  const ms = bootMark === null ? null : Math.round(performance.now() - bootMark);
  bootMark = null;
  const detail = KNOWN_FAILURES.has(code) ? code : 'other';
  track('boot-fail', ms === null ? { detail } : { detail, value: ms });
  // The phone page is a place of its own; any other failure leaves the
  // learner on the course, looking at the apology.
  if (watching) setPlace({ where: code === 'phone' ? 'phone' : 'course' });
}
